/**
 * Bediener (Kassierkräfte) je Kasse, lokal am Gerät gepflegt: eine Namensliste
 * und der gerade aktive Name, der mit jedem Beleg an den Server geht.
 * Kein Zugriffsschutz – die Zuordnung dient Tagesbericht und Journal.
 */
export interface Speicher {
  getItem(schluessel: string): string | null;
  setItem(schluessel: string, wert: string): void;
}

export interface BedienerStand {
  liste: string[];
  aktiv: string | null;
}

export const BEDIENER_MAX_LAENGE = 60;

function schluessel(kassenId: string): string {
  return `bilanx.bediener.${kassenId}`;
}

export function ladeBediener(speicher: Speicher, kassenId: string): BedienerStand {
  const roh = speicher.getItem(schluessel(kassenId));
  if (roh === null) {
    return { liste: [], aktiv: null };
  }
  try {
    const x: unknown = JSON.parse(roh);
    if (typeof x !== 'object' || x === null) {
      return { liste: [], aktiv: null };
    }
    const o = x as Record<string, unknown>;
    const liste = Array.isArray(o.liste)
      ? o.liste.filter((n): n is string => typeof n === 'string' && n.trim() !== '')
      : [];
    const aktiv = typeof o.aktiv === 'string' && liste.includes(o.aktiv) ? o.aktiv : null;
    return { liste, aktiv };
  } catch {
    return { liste: [], aktiv: null };
  }
}

export function speichereBediener(
  speicher: Speicher,
  kassenId: string,
  stand: BedienerStand,
): void {
  speicher.setItem(schluessel(kassenId), JSON.stringify(stand));
  meldeBedienerwechsel(kassenId);
}

/** Ereignis für die Kopfzeile: gewählter oder angemeldeter Bediener hat sich geändert. */
export const BEDIENER_EREIGNIS = 'bilanx:bediener-gewechselt';

function meldeBedienerwechsel(kassenId: string): void {
  if (typeof window !== 'undefined' && typeof CustomEvent === 'function') {
    window.dispatchEvent(new CustomEvent(BEDIENER_EREIGNIS, { detail: { kassenId } }));
  }
}

/** Namen bereinigen: getrimmt, auf 60 Zeichen gekürzt; null bei leerer Eingabe. */
export function bereinigeName(name: string): string | null {
  const t = name.trim().replace(/\s+/g, ' ').slice(0, BEDIENER_MAX_LAENGE);
  return t === '' ? null : t;
}

export function fuegeBedienerHinzu(stand: BedienerStand, name: string): BedienerStand {
  const n = bereinigeName(name);
  if (n === null || stand.liste.some((x) => x.toLowerCase() === n.toLowerCase())) {
    return stand;
  }
  return { liste: [...stand.liste, n], aktiv: stand.aktiv ?? n };
}

export function entferneBediener(stand: BedienerStand, name: string): BedienerStand {
  const liste = stand.liste.filter((x) => x !== name);
  return { liste, aktiv: stand.aktiv === name ? (liste[0] ?? null) : stand.aktiv };
}

export function setzeAktivenBediener(stand: BedienerStand, name: string | null): BedienerStand {
  return { ...stand, aktiv: name !== null && stand.liste.includes(name) ? name : null };
}

/* ── Bediener vom Server (Backoffice-Verwaltung mit PIN und Rechten) ────────── */

export interface BedienerRechte {
  storno: boolean;
  rabatt: boolean;
  tagesabschluss: boolean;
  /**
   * Retoure Stufe 2: Recht `retoure`, nur wenn der Server es mitliefert (sonst unbekannt – dann
   * entscheidet allein der Server in der Vorschau bzw. beim Abschluss).
   */
  retoure?: boolean;
}

/** Ohne Verwaltung am Server gilt: alles erlaubt (wie bisher). */
export const ALLE_RECHTE: BedienerRechte = { storno: true, rabatt: true, tagesabschluss: true };
/** Verwaltung aktiv, aber niemand angemeldet: nichts erlaubt. */
export const KEINE_RECHTE: BedienerRechte = { storno: false, rabatt: false, tagesabschluss: false };

export interface AngemeldeterBediener {
  id: number;
  name: string;
  rechte: BedienerRechte;
  /** Vom Server signierte Anmeldung; nötig für Storno und Rückerstattung. */
  sitzung?: string;
}

function anmeldeSchluessel(kassenId: string): string {
  return `bilanx.bediener.angemeldet.${kassenId}`;
}

/** Angemeldeten Bediener der Sitzung lesen (sessionStorage: Tab zu = abgemeldet). */
export function ladeAnmeldung(speicher: Speicher, kassenId: string): AngemeldeterBediener | null {
  const roh = speicher.getItem(anmeldeSchluessel(kassenId));
  if (roh === null || roh === '') {
    return null;
  }
  try {
    const x: unknown = JSON.parse(roh);
    if (typeof x !== 'object' || x === null) {
      return null;
    }
    const o = x as Record<string, unknown>;
    const r = (typeof o.rechte === 'object' && o.rechte !== null ? o.rechte : {}) as Record<
      string,
      unknown
    >;
    if (typeof o.id !== 'number' || typeof o.name !== 'string') {
      return null;
    }
    return {
      id: o.id,
      name: o.name,
      rechte: {
        storno: r.storno === true,
        rabatt: r.rabatt === true,
        tagesabschluss: r.tagesabschluss === true,
        ...(typeof r.retoure === 'boolean' ? { retoure: r.retoure } : {}),
      },
      ...(typeof o.sitzung === 'string' ? { sitzung: o.sitzung } : {}),
    };
  } catch {
    return null;
  }
}

/** PB-3: widerruft ein abgelegtes Bediener-Kennzeichen am Server (registriert die App). */
export type AbmeldeMelder = (kassenId: string, sitzung: string) => void;
let abmeldeMelder: AbmeldeMelder | null = null;

/**
 * Melder setzen (App-Start); liefert die Aufräumfunktion für den React-Effekt. Der Melder darf
 * nicht warten oder werfen – das Abmelden am Gerät gilt sofort, der Server folgt, sobald er
 * erreichbar ist (offline: Kennzeichen bleibt bis zum Ablauf gültig, liegt aber nicht mehr hier).
 */
export function setzeAbmeldeMelder(melder: AbmeldeMelder | null): () => void {
  abmeldeMelder = melder;
  return () => {
    if (abmeldeMelder === melder) {
      abmeldeMelder = null;
    }
  };
}

export function speichereAnmeldung(
  speicher: Speicher,
  kassenId: string,
  bediener: AngemeldeterBediener | null,
): void {
  const bisher = ladeAnmeldung(speicher, kassenId)?.sitzung;
  speicher.setItem(anmeldeSchluessel(kassenId), bediener === null ? '' : JSON.stringify(bediener));
  meldeBedienerwechsel(kassenId);
  // PB-3: Abmelden, Bedienerwechsel und automatische Abmeldung widerrufen das bisherige
  // Kennzeichen auch am Server (Abmelden-Taste, Wechsel und Leerlauf-Wächter laufen hier durch).
  if (bisher !== undefined && bisher !== bediener?.sitzung) {
    try {
      abmeldeMelder?.(kassenId, bisher);
    } catch {
      // Abmelden am Gerät gilt trotzdem
    }
  }
}

function verwaltetSchluessel(kassenId: string): string {
  return `bilanx.bediener.verwaltet.${kassenId}`;
}

/** Merken, ob der Server Bediener für diese Kasse verwaltet (dann gilt ohne Anmeldung kein Recht). */
export function speichereVerwaltet(speicher: Speicher, kassenId: string, verwaltet: boolean): void {
  speicher.setItem(verwaltetSchluessel(kassenId), verwaltet ? '1' : '');
}

export function istVerwaltet(speicher: Speicher, kassenId: string): boolean {
  return speicher.getItem(verwaltetSchluessel(kassenId)) === '1';
}

/** Name für den Beleg: angemeldeter Bediener, sonst der lokal gewählte. */
export function aktiverBedienerName(
  sitzung: Speicher,
  lokal: Speicher,
  kassenId: string,
): string | null {
  return ladeAnmeldung(sitzung, kassenId)?.name ?? ladeBediener(lokal, kassenId).aktiv;
}

/** Rechte der Sitzung: die des angemeldeten Bedieners; ohne Anmeldung keine bei aktiver Verwaltung, sonst alle. */
export function aktuelleRechte(sitzung: Speicher, kassenId: string): BedienerRechte {
  return (
    ladeAnmeldung(sitzung, kassenId)?.rechte ??
    (istVerwaltet(sitzung, kassenId) ? KEINE_RECHTE : ALLE_RECHTE)
  );
}

/** Nach so langer Ruhe wird ein angemeldeter Bediener abgemeldet (Schichtwechsel, unbeaufsichtigtes Gerät). */
export const ABMELDUNG_NACH_MS = 15 * 60_000;

/** Ereignis am `window`, mit dem der Wächter die Oberfläche über die Abmeldung informiert. */
export const ABMELDUNG_EREIGNIS = 'bilanx:bediener-abgemeldet';

/** true, wenn seit der letzten Bedienung mehr als `limitMs` vergangen sind. */
export function abmeldungFaellig(
  letzteAktivitaetMs: number,
  jetztMs: number,
  limitMs = ABMELDUNG_NACH_MS,
): boolean {
  return jetztMs - letzteAktivitaetMs >= limitMs;
}

/**
 * Wächter für die automatische Abmeldung: merkt sich jede Bedienung (Tippen,
 * Tasten) und meldet den Bediener der Kasse nach `limitMs` Ruhe ab. Liefert
 * die Aufräumfunktion für den React-Effekt.
 */
export function starteAbmeldeWaechter(
  sitzung: Speicher,
  kassenId: string,
  limitMs = ABMELDUNG_NACH_MS,
  pruefIntervallMs = 30_000,
): () => void {
  let letzteAktivitaet = Date.now();
  const aktivitaet = (): void => {
    letzteAktivitaet = Date.now();
  };
  const ereignisse = ['pointerdown', 'keydown', 'touchstart'] as const;
  for (const e of ereignisse) {
    window.addEventListener(e, aktivitaet, { passive: true });
  }
  const timer = setInterval(() => {
    if (
      ladeAnmeldung(sitzung, kassenId) !== null &&
      abmeldungFaellig(letzteAktivitaet, Date.now(), limitMs)
    ) {
      speichereAnmeldung(sitzung, kassenId, null);
      window.dispatchEvent(new CustomEvent(ABMELDUNG_EREIGNIS, { detail: { kassenId } }));
    }
  }, pruefIntervallMs);
  return () => {
    clearInterval(timer);
    for (const e of ereignisse) {
      window.removeEventListener(e, aktivitaet);
    }
  };
}

/** Initialen für Bediener- und Kunden-Chips („Max Muster“ → „MM“), höchstens zwei Zeichen. */
export function initialen(name: string): string {
  return name
    .trim()
    .split(/\s+/)
    .map((t) => t[0] ?? '')
    .join('')
    .slice(0, 2)
    .toUpperCase();
}
